// Attribution: https://jasonwatmore.com/post/2020/04/18/fetch-a-lightweight-fetch-wrapper-to-simplify-http-requests

// Services
import { getToken } from "@/services/auth";

export interface FetchOptions {
  headers?: Record<string, string>;
  query?: Record<string, string>;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  useCredentials?: boolean;
  body?: string | FormData;
  returnType?: "text" | "blob" | "raw";
  GET?: boolean;
  signal?: AbortSignal;
}

function sanitizeErrorMessage(message: string, status: number): string {
  if (status >= 500) {
    return "An error occurred. Please try again later.";
  }

  if (status >= 400 && status < 500) {
    const sanitized = message
      ?.replace(/at\s+.*?\n/g, "")
      .replace(/\s+at\s+.*$/g, "")
      .replace(/Error:\s*/g, "")
      .replace(/\n\s*at\s+.*$/g, "")
      .trim();

    return sanitized || "A client error occurred. Please check your request.";
  }

  return message;
}

// Helper function to handle JSON/text responses
async function handleTextResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  try {
    const data = text && JSON.parse(text); // Attempt to parse JSON

    if (!response.ok) {
      // Prefer error structure from API if available, otherwise use status text or raw text
      const rawErrorMessage =
        data?.message ||
        data?.error ||
        (typeof data === "string" ? data : null) ||
        text ||
        `HTTP error! status: ${response.status} ${response.statusText}`;

      const finalMessage =
        typeof rawErrorMessage === "string"
          ? rawErrorMessage
          : JSON.stringify(rawErrorMessage);

      const sanitizedMessage = sanitizeErrorMessage(
        finalMessage,
        response.status,
      );

      throw new Error(sanitizedMessage);
    }
    return data as T; // Return parsed data if successful
  } catch (e) {
    // This catch handles JSON.parse failure or if response.ok was true but text wasn't valid JSON
    // Also catches the error thrown from !response.ok block
    // console.error("Response handling/parsing error:", e.message, "Raw text:", text);

    if (e instanceof Error && e.message) {
      throw e;
    }

    throw new Error("Failed to parse response.", { cause: e });
  }
}

// Helper function to handle blob responses
async function handleBlobResponse(response: Response) {
  if (!response.ok) {
    // For blob responses, provide sanitized error messages based on status code
    const sanitizedMessage = sanitizeErrorMessage(
      `HTTP error! status: ${response.status} ${response.statusText}`,
      response.status,
    );
    throw new Error(sanitizedMessage);
  }
  return response.blob();
}

/* eslint-disable no-redeclare */
// TypeScript function overloads for type safety
export async function fetchWrapper<T>(
  url: string,
  options: FetchOptions & { returnType: "text" },
  scopes?: string[] | null,
): Promise<T>;
export async function fetchWrapper(
  url: string,
  options: FetchOptions & { returnType: "blob" },
  scopes?: string[] | null,
): Promise<Blob>;
export async function fetchWrapper(
  url: string,
  options: FetchOptions & { returnType: "raw" },
  scopes?: string[] | null,
): Promise<Response>;
export async function fetchWrapper<T = any>(
  url: string,
  options?: FetchOptions,
  scopes?: string[] | null,
): Promise<T>;
export async function fetchWrapper<T = unknown>(
  url: string,
  options: FetchOptions = {},
  scopes: string[] | null = null,
): Promise<T | Blob | Response> {
  /* eslint-enable no-redeclare */
  const {
    headers: optionHeaders = {},
    query = null,
    method: initialMethod = "GET", // The method the caller intends
    useCredentials = false,
    body,
    returnType = "text", // 'text' for JSON/plain text, 'blob' for binary
    signal,
  } = options;

  const requestHeaders = { ...optionHeaders };
  let actualUrl = url;
  let effectiveMethod = initialMethod;

  // Apply Content-Type only when a body is present (and not FormData),
  // unless explicitly overridden by the caller. This avoids sending
  // Content-Type for GET requests without a body, which can trigger
  // unnecessary CORS preflight requests on some third-party APIs.
  if (
    body !== undefined &&
    body !== null &&
    !(body instanceof FormData) &&
    !requestHeaders["Content-Type"]
  ) {
    requestHeaders["Content-Type"] = "application/json";
  }

  // MSAL Token Acquisition
  if (scopes) {
    try {
      const token = await getToken(scopes);
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }
    } catch {
      throw new Error("Authentication token acquisition failed."); // Propagate error
    }
  }

  // Prepare Body
  let processedBody = body;
  if (
    body &&
    !(body instanceof FormData) &&
    typeof body !== "string" &&
    requestHeaders["Content-Type"] === "application/json"
  ) {
    processedBody = JSON.stringify(body);
  }

  // Query String
  if (query) {
    const queryString = new URLSearchParams(query).toString();
    if (queryString) {
      actualUrl = `${url}?${queryString}`;
    }
  }

  // HTTP Method Override for GET requests with a body (tunneling)
  // options.GET being true is a custom flag indicating the user wants to tunnel a GET via POST
  if (processedBody && initialMethod === "GET" && options.GET === true) {
    effectiveMethod = "POST"; // Change method to POST for tunneling
    requestHeaders["X-HTTP-Method-Override"] = "GET";
  }

  const requestOptions: RequestInit = {
    method: effectiveMethod,
    headers: requestHeaders,
    body: processedBody,
  };

  if (useCredentials) {
    requestOptions.credentials = "include";
  }

  if (signal) {
    requestOptions.signal = signal;
  }

  const response = await fetch(actualUrl, requestOptions);

  if (response.status === 503 || response.status === 502) {
    window.dispatchEvent(new Event("site-outage-detected"));
    throw new Error("SiteUnavailable");
  }

  if (returnType === "blob") {
    return await handleBlobResponse(response);
  }

  if (returnType === "raw") {
    return response;
  }
  // Default to text/JSON handling
  return await handleTextResponse<T>(response);
}
